import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type ColumnDefinition, ReactTabulator } from 'react-tabulator';
import type { Tabulator } from 'react-tabulator/lib/types/TabulatorTypes';
import type { CellComponent, RowComponent } from 'tabulator-tables';
import DetailInfoModalLog from '@/domains/log/components/DetailInfoModalLog/DetailInfoModalLog';
import useEnvLog from '@/domains/log/hooks/useEnvLog';
import type { LogEnvListData } from '@/domains/log/types/env-log.types';
import { remoteSortHandler, useServerTable } from '@/shared/hooks/useServerTable';

const IndoorAirTabulator = [
  {
    title: 'No',
    formatter: (cell: CellComponent) => {
      const row = cell.getRow();
      const page = row.getTable().getPage();
      const pageSize = row.getTable().getPageSize();
      return (page - 1) * pageSize + row.getPosition(true);
    },
    width: 60,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
  },
  {
    title: '수집시간',
    field: 'aggregated_time',
    hozAlign: 'center',
    widthGrow: 2,
    headerHozAlign: 'center',
    headerSort: true,
    resizable: false,
  },
  {
    title: '사이트',
    field: 'site_name',
    widthGrow: 1,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
  },
  {
    title: '센서명',
    field: 'sensor_name',
    widthGrow: 1,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
  },
  {
    title: '미세먼지(ug/m³)',
    field: 'fine_dust',
    widthGrow: 1,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
    formatter: (cell: CellComponent) => {
      const value = cell.getValue();
      return value === null || value === undefined ? '-' : value;
    },
  },
  {
    title: '초미세먼지(ug/m³)',
    field: 'ultra_fine_dust',
    widthGrow: 1,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
    formatter: (cell: CellComponent) => {
      const value = cell.getValue();
      return value === null || value === undefined ? '-' : value;
    },
  },
  {
    title: '온도(℃)',
    field: 'temp',
    widthGrow: 1,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
    formatter: (cell: CellComponent) => {
      const value = cell.getValue();
      return value === null || value === undefined ? '-' : value;
    },
  },
  {
    title: '습도(%)',
    field: 'humidity',
    widthGrow: 1,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
    formatter: (cell: CellComponent) => {
      const value = cell.getValue();
      return value === null || value === undefined ? '-' : value;
    },
  },

  {
    title: '이산화탄소(ppm)',
    field: 'carbon_dioxide',
    widthGrow: 1,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
    formatter: (cell: CellComponent) => {
      const value = cell.getValue();
      return value === null || value === undefined ? '-' : value;
    },
  },
  {
    title: '휘발성유기화합물(ppm)',
    field: 'volatile_organic',
    widthGrow: 1,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
    formatter: (cell: CellComponent) => {
      const value = cell.getValue();
      return value === null || value === undefined ? '-' : value;
    },
  },
  {
    title: '일산화탄소(ppm)',
    field: 'carbon_monoxide',
    widthGrow: 1,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
    formatter: (cell: CellComponent) => {
      const value = cell.getValue();
      return value === null || value === undefined ? '-' : value;
    },
  },
  {
    title: '포름알데히드(ppm)',
    field: 'formaldehyde',
    widthGrow: 1,
    hozAlign: 'center',
    headerHozAlign: 'center',
    headerSort: false,
    resizable: false,
    formatter: (cell: CellComponent) => {
      const value = cell.getValue();
      return value === null || value === undefined ? '-' : value;
    },
  },
];

const mapIndoorRows = (rows: LogEnvListData['rows'] = []) => {
  return rows.map((row) => ({
    id: row.request_id,
    aggregated_time: row.timestamp,
    site_name: row.site_name,
    sensor_name: row.sensor_name,
    fine_dust: row.pm10 ?? null,
    ultra_fine_dust: row.pm25 ?? null,
    temp: row.temp ?? null,
    humidity: row.humi ?? null,
    carbon_dioxide: row.co2 ?? null,
    volatile_organic: row.vocs ?? null,
    carbon_monoxide: row.co ?? null,
    formaldehyde: row.hcho ?? null,
    request_id: row.request_id,
  }));
};

interface IndoorAirLogProps {
  filterParams: string;
  pageSize: number;
}

const IndoorAirLog = ({ filterParams, pageSize }: IndoorAirLogProps) => {
  const { t } = useTranslation();
  const tabulatorRef = useRef<Tabulator | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);

  // Filters (sensor_type/date range/search) are owned by the parent and passed
  // in as `filterParams`; this table only manages page/size/sort on top of them.
  const table = useServerTable({
    defaultSort: { field: 'aggregated_time', dir: 'desc' },
    defaultSize: pageSize,
  });
  // Keep page size in sync with the parent-controlled prop.
  // biome-ignore lint/correctness/useExhaustiveDependencies: setSize is stable
  useEffect(() => {
    table.setSize(pageSize);
  }, [pageSize]);
  // Reset to the first page whenever the parent filter changes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: setPage is stable
  useEffect(() => {
    table.setPage(1);
  }, [filterParams]);

  const combinedQuery = `${filterParams}&${table.queryString}`;
  const { logEnvListData, logEnvDetailData } = useEnvLog({
    queryParams: combinedQuery,
    request_id: selectedRequestId,
  });
  const pageData = logEnvListData?.data;
  const rows = useMemo(() => mapIndoorRows(pageData?.rows ?? []), [pageData]);

  const languageTabulator = useCallback(() => {
    return {
      pagination: {
        first: t('cmn > first page'),
        first_title: t('cmn > first page'),
        last: t('cmn > last page'),
        last_title: t('cmn > last page'),
        prev: t('cmn > page before'),
        prev_title: t('cmn > page before'),
        next: t('cmn > next page'),
        next_title: t('cmn > next page'),
      },
    };
  }, [t]);

  const placeholder = "<div style='text-align:center;'> No Data</div>";

  // Override the module column "No" formatter to use the server page/size.
  const columns = useMemo(() => {
    const cols = [...IndoorAirTabulator] as unknown as ColumnDefinition[];
    cols[0] = {
      ...cols[0],
      formatter: (cell: CellComponent) =>
        String((table.page - 1) * table.size + cell.getRow().getPosition(true)),
    } as ColumnDefinition;
    return cols;
  }, [table.page, table.size]);

  const optionsTabulator = useMemo(
    () => ({
      // Server-side pagination: Tabulator's own pager is off; <TablePagination> drives it.
      pagination: false,
      rowHeight: 41,
      movableRows: false,
      index: 'id',
      locale: 'ko',
      langs: {
        ko: languageTabulator(),
      },
      resizableRows: false,
      placeholder,
    }),
    [languageTabulator]
  );

  const handleRowClick = (request_id: string) => {
    setSelectedRequestId(request_id);
    setIsModalOpen(true);
  };

  return (
    <section className="w-full h-full relative">
      <div className=" w-full h-full">
        <ReactTabulator
          data={rows}
          columns={columns as unknown as ColumnDefinition[]}
          layout={'fitColumns'}
          className="tabulator-custom w-full "
          options={optionsTabulator}
          events={{
            rowClick: (_e: UIEvent, row: RowComponent) => {
              // setSelectedRow(row.getData());
              const request_id = row.getData()?.request_id;
              if (request_id) {
                handleRowClick(request_id);
              }
            },
            // Header click → delegate sorting to the server (only aggregated_time).
            dataSorted: remoteSortHandler(table, {
              aggregated_time: 'aggregated_time',
            }),
          }}
          onRef={(ref) => {
            tabulatorRef.current = ref.current;
          }}
        />
        {/* Server-side Pagination Controls - Tabulator style */}
        <div className="table-container relative">
          <div className="tabulator-footer">
            <div className="tabulator-paginator">
              <button
                type="button"
                className="tabulator-page"
                onClick={() => table.setPage(1)}
                disabled={table.page === 1}
              >
                {t('cmn > first page')}
              </button>
              <button
                type="button"
                className="tabulator-page"
                onClick={() => table.setPage(table.page - 1)}
                disabled={table.page === 1}
              >
                {t('cmn > page before')}
              </button>
              <span className="tabulator-pages">
                {Array.from(
                  { length: Math.min(5, pageData?.total_pages ?? 0) },
                  (_, i) => {
                    const totalPages = pageData?.total_pages ?? 0;
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (table.page <= 3) {
                      pageNum = i + 1;
                    } else if (table.page >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = table.page - 2 + i;
                    }
                    return (
                      <button
                        type="button"
                        key={pageNum}
                        className={`tabulator-page ${table.page === pageNum ? 'active' : ''}`}
                        onClick={() => table.setPage(pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  }
                )}
              </span>
              <button
                type="button"
                className="tabulator-page"
                onClick={() => table.setPage(table.page + 1)}
                disabled={table.page === (pageData?.total_pages ?? 0)}
              >
                {t('cmn > next page')}
              </button>
              <button
                type="button"
                className="tabulator-page"
                onClick={() => table.setPage(pageData?.total_pages ?? 1)}
                disabled={table.page === (pageData?.total_pages ?? 0)}
              >
                {t('cmn > last page')}
              </button>
            </div>
            <div style={{ padding: '0 20px 0 0', textAlign: 'right' }}>
              총 {pageData?.total ?? 0} 건
            </div>
          </div>
        </div>
      </div>
      <DetailInfoModalLog
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedRequestId(null);
        }}
        logData={logEnvDetailData?.data}
      />
    </section>
  );
};

export default IndoorAirLog;
